/**
 * Auto-injection of field codecs — the single owner of the "explicit codec
 * wins" invariant and of the per-type opt-in rules.
 *
 * One table entry per codec-carrying field type. The six types are mutually
 * exclusive per field (a field has exactly one `type`), so a single pass with
 * at-most-one matching entry is behaviorally identical to the six sequential
 * per-type passes it replaced.
 */

import { createFileCodec } from './file-codec.js';
import { createMultiRelationCodec } from './multi-relation-codec.js';
import { createVideosCodec } from './videos-codec.js';
import { createFormStructureCodec } from './form-structure-codec.js';
import { createYesnoCodec } from './yesno-codec.js';
import { createNumericIdCodec } from './numeric-id-codec.js';

/**
 * @typedef {Object} CodecInjection
 * @property {string} type - FieldDef `type` this entry targets.
 * @property {(field: Record<string, unknown>) => boolean} [when] - Additional
 *   opt-in gate (truthiness of a field flag). Absent ⇒ every field of the type.
 * @property {(field: Record<string, unknown>) => import('../../contracts/forms.js').FieldCodec} create
 */

/** @type {CodecInjection[]} */
const CODEC_INJECTIONS = [
  {
    type: 'file',
    create: (f) => createFileCodec(/** @type {boolean | undefined} */ (f.multiple)),
  },
  {
    type: 'videos',
    create: () => createVideosCodec(),
  },
  {
    type: 'form-builder',
    create: () => createFormStructureCodec(),
  },
  {
    // Converts between the legacy wire format (array of objects with id/pid
    // keys) and the UI format (flat string array of IDs).
    type: 'multi-relation',
    create: (f) => createMultiRelationCodec(/** @type {string} */ (f.valueProp ?? 'id')),
  },
  {
    // Opt-in only (`numeric: true`) so the value persists as integer 1/0 —
    // fields whose backend stores '1'/'0' strings keep the default (no codec).
    type: 'yesno',
    when: (f) => Boolean(f.numeric),
    create: () => createYesnoCodec(),
  },
  {
    // Opt-in only (`numericId: true`) so a numeric-string id persists as a
    // number — string (slug) FKs must not be coerced. The empty-value policy
    // is carried by `numericIdEmptyValue` PRESENCE (present ⇒ send it, absent
    // ⇒ omit the key), hence the hasOwnProperty check rather than a ?? chain.
    type: 'async-select',
    when: (f) => Boolean(f.numericId),
    create: (f) => createNumericIdCodec(
      Object.prototype.hasOwnProperty.call(f, 'numericIdEmptyValue')
        ? { numericIdEmptyValue: /** @type {number | undefined} */ (f.numericIdEmptyValue) }
        : {},
    ),
  },
];

/**
 * Auto-inject codecs on fields whose type carries one and that opted in,
 * unless the field already provides an explicit codec (explicit codec wins —
 * checked HERE, once, for every entry). Fields with no matching entry, a
 * failed opt-in gate, or an explicit codec pass through by reference.
 *
 * @param {import('../../contracts/forms.js').FieldDef[]} fields
 * @returns {import('../../contracts/forms.js').FieldDef[]}
 */
export function injectFieldCodecs(fields) {
  return fields.map((f) => {
    const entry = CODEC_INJECTIONS.find((e) => e.type === f.type);
    if (!entry) return f;
    const raw = /** @type {Record<string, unknown>} */ (f);
    if (entry.when && !entry.when(raw)) return f;
    if (raw.codec) return f;
    return /** @type {import('../../contracts/forms.js').FieldDef} */ (
      { ...f, codec: entry.create(raw) }
    );
  });
}
